import * as THREE from 'three';
import { clamp, hash01, lerp, smoothstep } from '@/core/rng';
import { surfaceHeight } from '@/world/chunk';
import type { Region } from '@/world/region';
import {
  relicsIn, relicHeight, RELIC_KINDS, type Relic, type RelicKind,
} from '@/world/relics';

/**
 * The island's rare things, drawn.
 *
 * `world/relics.ts` decides *what stands where* — one election per 1600 m
 * super-cell, gated by the ground's own conditions, nothing stored. This draws
 * the result, and it is built exactly like `render/rocks.ts`: one walk of the
 * world at load, one InstancedMesh per kind, `frustumCulled = false` because a
 * single mesh spans the island and its bounds are the island, and **a linear
 * scan rather than a raycast** for the pointer. That last one is not a
 * preference: raycasting the island-wide rock mesh was measured at 8 ms a
 * frame, because every ray tests every instance inside an island-sized bounding
 * sphere. The scan over a couple of hundred relics is under a microsecond.
 *
 * ## The one rule this file must not break
 *
 * The pointer does not ask this module what is there. `main.ts` asks
 * `relicNear`, which recomputes the election from the lattice. So **every
 * relic this module draws must be one `relicNear` would find, and no others**.
 * That is why the build calls `relicsIn` rather than applying any placement
 * rule of its own, and why there is no "skip it if it would stand in the surf"
 * test here: `rocks.ts` can refuse to seat a boulder because its pointer reads
 * back the positions it actually composed, and this one cannot, because its
 * pointer is a lattice. Anything this file declined to draw would still have a
 * name and still be pointable, which is the pointer/picture disagreement the
 * litter layer already had to have fixed.
 *
 * What is left to the renderer is what the renderer is allowed to own: the
 * shapes, the tint, the seating on the real surface, and how far each kind is
 * worth drawing.
 */

/** Where a relic actually is now, when a god has moved it. */
export interface MovedRelic { key: string; x: number; z: number }

export interface Relics {
  group: THREE.Group;
  /**
   * Camera orbit radius in metres. Small kinds drop out long before big ones.
   *
   * `moved` is the god's hand: every relic that is no longer where the world
   * grew it, and where it is instead. It is passed rather than looked up
   * because this module may not import the simulation, and it is a VERSION
   * plus a list rather than a callback because the re-place is O(moved) and
   * only worth doing when something has actually changed.
   *
   * A moved relic keeps its own instance slot and simply stands somewhere
   * else. That is what makes this cheap — no rebuild, no capacity change, no
   * hole where it used to be — and it is also the only shape that cannot
   * produce the failure this had to avoid: the thing drawn twice, once at the
   * lattice position `relicsIn` will always return and once where the carried
   * store says it is.
   */
  update(camR: number, moved?: readonly MovedRelic[], version?: number): void;
  /**
   * The relic nearest a point — the same shape as `Rocks.nearestTo`, and here
   * for the same reason: a linear scan over a list the module already has,
   * never a ray. The pick path in `main.ts` uses `relicNear` instead (it is
   * cheaper still, being a floor divide), so this exists for callers that have
   * the renderer to hand and want the *drawn* thing, ring included.
   */
  nearestTo(x: number, z: number, within: number): { relic: Relic; y: number } | null;
  /** Everything found at load, in lattice order. Read-only; nothing is stored. */
  readonly all: readonly Relic[];
  dispose(): void;
}

/**
 * Metres of draw reach per metre of thing.
 *
 * The same angular convention `render/nearDetail.ts`'s landmark band already
 * uses (`FAR_REACH` = 340) and the same ~3 px floor `tools/settlement.ts` set
 * for a signal: at 340× its own size, a thing is about 3.1 px of this camera
 * (46° vertical over 900 px). A 4.3 m standing stone therefore carries ~1.5 km
 * and a 12.7 m lightning-struck snag ~4.3 km, which is the right asymmetry —
 * the tall dead tree is the one that should pull you off a course from the air.
 */
const REACH_PER_METRE = 340;
/** …and nothing is drawn from orbit however big it is. */
const VIS_CAP = 9000;

/** How wide on the ground, given the greatest dimension the world reports. */
function footprint(kind: RelicKind, size: number): number {
  switch (kind) {
    case 'standing_stone': return size * 0.36;   // a slab on end is narrow
    case 'lightning_tree': return size * 0.30;   // trunk plus its broken limbs
    case 'meteorite': return size;               // `size` IS the crater
    case 'spring': return size;
    case 'bone_field': return size;
    case 'cave_mouth': return size;
    case 'shell_midden': return size;
  }
}

// ---------------------------------------------------------------------------
// Shapes
//
// Every one is authored in the same unit box — y ∈ [0, 1], x and z in
// [−0.5, 0.5] — so the per-instance scale is (footprint, height, footprint) and
// nothing has to know how the shape was built. Vertex colour carries the hue
// AND the vertical shade ramp, because these are two-toned things: pale bone on
// dark earth, black char under silver deadwood, a dark eye of water inside a
// pale stone lip. The instance colour is then only a small per-relic variation,
// which is the opposite of the litter layer's split and is right for the same
// reason: there are two hundred of these, not two hundred thousand.
// ---------------------------------------------------------------------------

const ATTRS = [['position', 3], ['normal', 3], ['color', 3]] as const;

/** Triangle soup: everything merges non-indexed, like vegetation and litter. */
function solid(g: THREE.BufferGeometry): THREE.BufferGeometry {
  if (!g.index) return g;
  const flat = g.toNonIndexed();
  g.dispose();
  return flat;
}

/**
 * Shove a shell's vertices about by a hash of their own rounded position, so
 * the duplicates in the triangle soup move together and the shell never
 * cracks — `rocks.ts` learned this once and it is the same trick here.
 */
function rough(g: THREE.BufferGeometry, seed: number, amp: number): THREE.BufferGeometry {
  const a = g.attributes.position.array as Float32Array;
  for (let i = 0; i < g.attributes.position.count; i++) {
    const o = i * 3;
    const x = a[o], y = a[o + 1], z = a[o + 2];
    const kx = Math.round(x * 4096), ky = Math.round(y * 4096), kz = Math.round(z * 4096);
    a[o] = x + (hash01(kx, ky * 7919 + kz, seed) - 0.5) * amp;
    a[o + 1] = y + (hash01(ky, kz * 7919 + kx, seed + 1) - 0.5) * amp;
    a[o + 2] = z + (hash01(kz, kx * 7919 + ky, seed + 2) - 0.5) * amp;
  }
  return g;
}

/**
 * Paint one part: `rgb` at the top, darkened to `rgb × shadeLow` at `yLow`.
 * Call after every transform — it reads final positions — and it also drops
 * `uv`, because the merge below only carries three attributes and a stray one
 * makes the buffers disagree in length.
 */
function paint(
  g: THREE.BufferGeometry, rgb: readonly [number, number, number],
  yLow: number, yHigh: number, shadeLow: number,
): THREE.BufferGeometry {
  const p = g.attributes.position.array as ArrayLike<number>;
  const n = g.attributes.position.count;
  const col = new Float32Array(n * 3);
  const span = Math.max(1e-6, yHigh - yLow);
  for (let i = 0; i < n; i++) {
    const t = clamp((p[i * 3 + 1] - yLow) / span, 0, 1);
    const s = lerp(shadeLow, 1, t);
    col[i * 3] = rgb[0] * s; col[i * 3 + 1] = rgb[1] * s; col[i * 3 + 2] = rgb[2] * s;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  if (g.attributes.uv) g.deleteAttribute('uv');
  if (g.attributes.tangent) g.deleteAttribute('tangent');
  return g;
}

function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  for (const [name, size] of ATTRS) {
    const arrs = parts.map((p) => p.attributes[name].array as Float32Array);
    const all = new Float32Array(arrs.reduce((s, a) => s + a.length, 0));
    let o = 0;
    for (const a of arrs) { all.set(a, o); o += a.length; }
    geo.setAttribute(name, new THREE.BufferAttribute(all, size));
  }
  parts.forEach((p) => p.dispose());
  geo.computeBoundingSphere();
  return geo;
}

/** Normals per face, so everything here reads faceted like the rocks do. */
const faceted = (g: THREE.BufferGeometry): THREE.BufferGeometry => {
  g.computeVertexNormals();
  return g;
};

const STONE = [0.44, 0.44, 0.47] as const;
const DARK_STONE = [0.30, 0.30, 0.33] as const;
const EARTH = [0.30, 0.24, 0.17] as const;
const BONE = [0.80, 0.78, 0.70] as const;
const CHAR = [0.09, 0.08, 0.08] as const;
const DEADWOOD = [0.56, 0.54, 0.49] as const;
const IRON = [0.13, 0.11, 0.10] as const;
const WATER = [0.10, 0.18, 0.22] as const;
const SHELL = [0.74, 0.72, 0.66] as const;
const HOLE = [0.02, 0.02, 0.025] as const;

/**
 * A raised slab, narrower at the top, with three packing stones at its foot.
 *
 * The packing stones are the whole point of the shape. A monolith standing in
 * grass reads as a prop dropped on the ground; the ring of wedges around its
 * base is what says *somebody put this here and meant it to stay*, which is
 * the one true thing the pointer's note claims about it.
 */
function standingStoneGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const shaft = faceted(rough(
    solid(new THREE.CylinderGeometry(0.30, 0.46, 1, 5, 1, false)), 0x71c3, 0.09));
  shaft.translate(0, 0.5, 0);
  // a slab, not a post: squashed on one axis and leaning a little off true
  shaft.scale(1, 1, 0.52);
  shaft.rotateZ(0.045);
  parts.push(paint(shaft, STONE, 0, 1, 0.62));
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.7;
    const w = 0.16 + hash01(i, 3, 0x71c9) * 0.10;
    const s = faceted(rough(solid(new THREE.IcosahedronGeometry(0.5, 0)), 0x71d1 + i, 0.22));
    s.scale(w, w * 0.55, w);
    s.translate(Math.cos(a) * 0.30, w * 0.20, Math.sin(a) * 0.24);
    parts.push(paint(s, DARK_STONE, 0, 0.3, 0.58));
  }
  return merge(parts);
}

/** A rim of thrown-up earth, and the black iron that made it, still sitting in it. */
function meteoriteGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  // the rim: a flattened torus, so the crater is a ring rather than a bowl the
  // terrain would have to be dug out to hold. The ground is not deformed here —
  // `world/earth.ts` owns that — so the rim is the whole crater.
  const rim = solid(new THREE.TorusGeometry(0.40, 0.13, 4, 14));
  rim.rotateX(Math.PI / 2);
  rim.scale(1, 0.75, 1);
  rim.translate(0, 0.16, 0);
  parts.push(paint(faceted(rough(rim, 0x82e1, 0.05)), EARTH, 0, 0.34, 0.55));
  const lump = faceted(rough(solid(new THREE.IcosahedronGeometry(0.5, 0)), 0x82e7, 0.26));
  lump.scale(0.44, 0.5, 0.40);
  lump.translate(0.03, 0.20, -0.02);
  parts.push(paint(lump, IRON, 0, 0.45, 0.5));
  return merge(parts);
}

/** A dark eye of water inside a lip of wet stone, with the sedge it feeds. */
function springGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  // the water: a flat disc, standard-lit like everything else. Nothing here is
  // emissive and nothing is MeshBasicMaterial — either of those is a thing that
  // glows at midnight, and this world has had that bug before.
  const pool = solid(new THREE.CircleGeometry(0.33, 12));
  pool.rotateX(-Math.PI / 2);
  pool.translate(0, 0.44, 0);
  parts.push(paint(pool, WATER, 0.44, 0.44, 1));
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + hash01(i, 1, 0x93f1) * 0.6;
    const w = 0.14 + hash01(i, 2, 0x93f3) * 0.12;
    const r = 0.38 + hash01(i, 4, 0x93f5) * 0.08;
    const s = faceted(rough(solid(new THREE.IcosahedronGeometry(0.5, 0)), 0x93f7 + i, 0.24));
    s.scale(w, w * 0.9, w);
    s.translate(Math.cos(a) * r, w * 0.42, Math.sin(a) * r);
    parts.push(paint(s, STONE, 0, 0.7, 0.55));
  }
  return merge(parts);
}

/** A great many animals died in one place, and the ground kept them. */
function boneFieldGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 11; i++) {
    const a = hash01(i, 0, 0xa4b1) * Math.PI * 2;
    const r = Math.sqrt(hash01(i, 1, 0xa4b3)) * 0.44;
    const len = 0.14 + hash01(i, 2, 0xa4b5) * 0.20;
    const yaw = hash01(i, 3, 0xa4b7) * Math.PI * 2;
    const shaft = solid(new THREE.CylinderGeometry(0.018, 0.022, len, 4, 1, false));
    shaft.rotateZ(Math.PI / 2);
    shaft.rotateY(yaw);
    shaft.translate(Math.cos(a) * r, 0.05, Math.sin(a) * r);
    parts.push(paint(faceted(shaft), BONE, 0, 0.12, 0.6));
    // the knuckle at each end — what makes a stick read as a bone
    for (const e of [-0.5, 0.5]) {
      const k = solid(new THREE.IcosahedronGeometry(0.5, 0));
      k.scale(0.042, 0.036, 0.042);
      k.translate(Math.cos(a) * r + Math.cos(yaw) * len * e,
                  0.05, Math.sin(a) * r - Math.sin(yaw) * len * e);
      parts.push(paint(faceted(k), BONE, 0, 0.1, 0.62));
    }
  }
  // and one skull, because a field of ribs with nothing to look at you is a
  // pile of sticks
  const skull = faceted(rough(solid(new THREE.IcosahedronGeometry(0.5, 0)), 0xa4c1, 0.14));
  skull.scale(0.13, 0.10, 0.16);
  skull.translate(0.10, 0.06, -0.14);
  parts.push(paint(skull, BONE, 0, 0.14, 0.55));
  return merge(parts);
}

/** A standing snag: charcoal at the foot, silver deadwood above, and no crown. */
function lightningTreeGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  // the trunk is two segments so the char can end partway up rather than
  // fading across the whole height — a strike scars the bottom
  const low = solid(new THREE.CylinderGeometry(0.16, 0.22, 0.34, 6, 1, false));
  low.translate(0, 0.17, 0);
  parts.push(paint(faceted(rough(low, 0xb5c1, 0.03)), CHAR, 0, 0.34, 0.72));
  const high = solid(new THREE.CylinderGeometry(0.055, 0.16, 0.66, 6, 1, false));
  high.translate(0, 0.67, 0);
  // the top is snapped, not tapered to a point: shear it off at an angle
  {
    const a = high.attributes.position.array as Float32Array;
    for (let i = 0; i < high.attributes.position.count; i++) {
      const o = i * 3;
      if (a[o + 1] > 0.94) a[o + 1] = 0.90 + a[o] * 0.35 + a[o + 2] * 0.12;
    }
  }
  parts.push(paint(faceted(rough(high, 0xb5c7, 0.025)), DEADWOOD, 0.30, 1, 0.55));
  for (let i = 0; i < 2; i++) {
    const yaw = 1.1 + i * 2.4;
    const limb = solid(new THREE.CylinderGeometry(0.02, 0.055, 0.30, 4, 1, false));
    limb.rotateZ(1.0 - i * 0.35);
    limb.rotateY(yaw);
    limb.translate(0, 0.58 + i * 0.14, 0);
    parts.push(paint(faceted(limb), DEADWOOD, 0.4, 0.8, 0.6));
  }
  return merge(parts);
}

/**
 * A hole in the hill, framed by rock.
 *
 * The dark is a shallow BOX rather than a plane: a plane is single-sided, so
 * from behind the mouth you would see straight through the hillside, and a
 * cave you can see through is a cave that is not there.
 */
function caveMouthGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const dark = solid(new THREE.BoxGeometry(0.62, 0.70, 0.16));
  dark.translate(0, 0.35, -0.06);
  parts.push(paint(faceted(dark), HOLE, 0, 0.7, 1));
  // the brow and the two jambs: seven blocks arching over the opening
  for (let i = 0; i < 7; i++) {
    const t = i / 6;
    const a = Math.PI * (0.06 + t * 0.88);
    const w = 0.20 + hash01(i, 5, 0xc6d1) * 0.14;
    const b = faceted(rough(solid(new THREE.DodecahedronGeometry(0.5, 0)), 0xc6d5 + i, 0.20));
    b.scale(w, w, w * 0.9);
    b.translate(Math.cos(a) * 0.40, 0.06 + Math.sin(a) * 0.72, 0.10);
    parts.push(paint(b, STONE, 0, 1, 0.5));
  }
  // an apron of fallen stuff at the lip, so it grows out of the hill
  for (let i = 0; i < 5; i++) {
    const w = 0.09 + hash01(i, 7, 0xc6e1) * 0.09;
    const s = faceted(rough(solid(new THREE.IcosahedronGeometry(0.5, 0)), 0xc6e5 + i, 0.24));
    s.scale(w, w * 0.6, w);
    s.translate((hash01(i, 8, 0xc6e9) - 0.5) * 0.8, w * 0.25,
                0.24 + hash01(i, 9, 0xc6eb) * 0.22);
    parts.push(paint(s, DARK_STONE, 0, 0.3, 0.58));
  }
  return merge(parts);
}

/** A century of shells, in a low pale mound with its own scatter around it. */
function shellMiddenGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const mound = faceted(rough(solid(new THREE.IcosahedronGeometry(0.5, 0)), 0xd7f1, 0.16));
  mound.scale(0.92, 0.9, 0.84);
  mound.translate(0, 0.10, 0);
  // flatten anything that would hang below the ground into the ground
  {
    const a = mound.attributes.position.array as Float32Array;
    for (let i = 0; i < mound.attributes.position.count; i++) {
      if (a[i * 3 + 1] < 0) a[i * 3 + 1] = 0;
    }
  }
  parts.push(paint(mound, SHELL, 0, 0.55, 0.52));
  for (let i = 0; i < 10; i++) {
    const a = hash01(i, 0, 0xd7f5) * Math.PI * 2;
    const r = 0.44 + hash01(i, 1, 0xd7f7) * 0.16;
    const w = 0.05 + hash01(i, 2, 0xd7f9) * 0.06;
    const chip = faceted(solid(new THREE.IcosahedronGeometry(0.5, 0)));
    chip.scale(w, w * 0.3, w);
    chip.translate(Math.cos(a) * r, w * 0.15, Math.sin(a) * r);
    parts.push(paint(chip, SHELL, 0, 0.1, 0.7));
  }
  return merge(parts);
}

const GEOMETRY: Record<RelicKind, () => THREE.BufferGeometry> = {
  standing_stone: standingStoneGeometry,
  meteorite: meteoriteGeometry,
  spring: springGeometry,
  bone_field: boneFieldGeometry,
  lightning_tree: lightningTreeGeometry,
  cave_mouth: caveMouthGeometry,
  shell_midden: shellMiddenGeometry,
};

/** The tall, hard-edged ones earn a shadow; the flat ones cannot pay for one. */
const CASTS: Record<RelicKind, boolean> = {
  standing_stone: true, lightning_tree: true, cave_mouth: true,
  meteorite: false, spring: false, bone_field: false, shell_midden: true,
};

// ---------------------------------------------------------------------------

export function createRelics(region: Region): Relics {
  const group = new THREE.Group();
  group.name = 'relics';
  const half = region.extentMetres / 2;

  // The election, once, from the world — never a placement rule of this
  // module's own. See the note at the top: the pointer recomputes this from
  // the lattice, so anything drawn here that `relicNear` would not find is a
  // bug by construction.
  const all = relicsIn(-half, -half, half, half, (x, z) => region.siteAt(x, z));

  const material = new THREE.MeshStandardMaterial({
    roughness: 0.92, metalness: 0, vertexColors: true, flatShading: true,
  });

  // build-time scratch; nothing here runs per frame
  const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler();
  const _p = new THREE.Vector3(), _s = new THREE.Vector3(), _c = new THREE.Color();

  const meshes: THREE.InstancedMesh[] = [];
  const geos: THREE.BufferGeometry[] = [];
  /** Per kind, the reach its biggest member earns — see REACH_PER_METRE. */
  const kindReach: number[] = [];
  /**
   * Flat parallel arrays for `nearestTo`: no per-call allocation, ever.
   *
   * Indexed by `all`'s own LATTICE order, not by the per-kind mesh order the
   * loop below builds in. Those two orders are different — a mesh holds one
   * kind and the lattice interleaves them — and letting the scan inherit the
   * mesh order would have made `Relics.all` a per-kind grouping while its
   * documentation said lattice order. `test/relics.ts` compares this list
   * against `relicsIn` element for element, and caught exactly that.
   */
  const posX = new Float64Array(all.length);
  const posY = new Float64Array(all.length);
  const posZ = new Float64Array(all.length);
  const posR = new Float64Array(all.length);
  for (let i = 0; i < all.length; i++) {
    const r = all[i];
    posX[i] = r.x; posZ[i] = r.z;
    posY[i] = surfaceHeight(region, r.x, r.z, 1);
    posR[i] = Math.max(footprint(r.kind, r.size), relicHeight(r.kind, r.size)) * 0.5;
  }

  for (const kind of RELIC_KINDS) {
    const mine = all.filter((r) => r.kind === kind);
    const geo = GEOMETRY[kind]();
    geos.push(geo);
    // capacity is the exact count: this is built once from an immutable world,
    // so there is nothing for a spare slot to ever hold
    const mesh = new THREE.InstancedMesh(geo, material, Math.max(1, mine.length));
    mesh.name = `relic_${kind}`;
    mesh.count = mine.length;
    // one mesh spans the island, so its bounds are the island: culling it by
    // them can only ever throw the whole kind away at the wrong moment
    mesh.frustumCulled = false;
    mesh.receiveShadow = true;
    if (CASTS[kind]) mesh.castShadow = true;

    let biggest = 0;
    for (let i = 0; i < mine.length; i++) {
      const r = mine[i];
      const w = footprint(kind, r.size);
      const h = relicHeight(kind, r.size);
      biggest = Math.max(biggest, r.size);
      const y = surfaceHeight(region, r.x, r.z, 1);
      const kx = Math.round(r.x), kz = Math.round(r.z);
      // yaw and tint are the renderer's own business, but they are still pure
      // functions of the relic's position — the same stone faces the same way
      // on every machine, for ever
      const yaw = hash01(kx, kz, 0xe1a3) * Math.PI * 2;
      // sunk a little, so the ground closes over the foot and nothing reads as
      // dropped on the surface — `rocks.ts`'s lesson, at this scale
      const bury = h * 0.05;
      _p.set(r.x, y - bury, r.z);
      _e.set(0, yaw, 0);
      _q.setFromEuler(_e);
      _s.set(w, h + bury, w);
      _m.compose(_p, _q, _s);
      mesh.setMatrixAt(i, _m);
      // a narrow band of variation, warmed on dry ground and cooled on wet:
      // the geometry already carries the hue, so this may only ever nudge it
      const site = region.siteAt(r.x, r.z);
      const dry = 1 - smoothstep(0.25, 0.75, site.rain);
      const v = 0.88 + hash01(kz, kx, 0xe1a9) * 0.22;
      _c.setRGB(v * (1 + dry * 0.06), v, v * (1 - dry * 0.05));
      mesh.setColorAt(i, _c);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    kindReach.push(Math.min(VIS_CAP, REACH_PER_METRE * Math.max(1, biggest)));
    meshes.push(mesh);
    group.add(mesh);
  }

  /** Per kind, the relics in that mesh's own instance order — so a moved one
   *  can be found and re-seated without rebuilding anything. */
  const byKind = RELIC_KINDS.map((kind) => all.filter((r) => r.kind === kind));
  /** The last carried-store version this re-seated for; -1 forces the first. */
  let seenMoved = -1;
  /** Keys currently displaced, so a relic PUT BACK returns to its own ground. */
  let displaced = new Set<string>();

  const seat = (kind: number, i: number, r: Relic, x: number, z: number): void => {
    const w = footprint(r.kind, r.size);
    const h = relicHeight(r.kind, r.size);
    const y = surfaceHeight(region, x, z, 1);
    // yaw stays the relic's OWN — a stone a god has carried is the same stone
    // and faces the way it always did. Keying it to the new position would
    // make the thing spin as it was set down, which is an editor's tell.
    const yaw = hash01(Math.round(r.x), Math.round(r.z), 0xe1a3) * Math.PI * 2;
    const bury = h * 0.05;
    _p.set(x, y - bury, z);
    _e.set(0, yaw, 0);
    _q.setFromEuler(_e);
    _s.set(w, h + bury, w);
    _m.compose(_p, _q, _s);
    meshes[kind].setMatrixAt(i, _m);
  };

  return {
    group,
    all,
    update(camR, moved, version) {
      // each kind carries as far as its biggest member can pay for. A field of
      // bones is gone from the air long before a lightning-struck snag is.
      for (let k = 0; k < meshes.length; k++) {
        meshes[k].visible = meshes[k].count > 0 && camR < kindReach[k];
      }
      if (moved === undefined || version === seenMoved) return;
      seenMoved = version ?? seenMoved;
      const now = new Map(moved.map((m) => [m.key, m]));
      const touched = new Set<number>();
      for (let k = 0; k < byKind.length; k++) {
        const mine = byKind[k];
        for (let i = 0; i < mine.length; i++) {
          const r = mine[i];
          const to = now.get(r.key);
          // moved, or PUT BACK — the second case is why `displaced` is kept.
          // Without it a relic returned to its own ground would stay drawn
          // wherever it was last set down, for ever.
          if (to) { seat(k, i, r, to.x, to.z); touched.add(k); }
          else if (displaced.has(r.key)) { seat(k, i, r, r.x, r.z); touched.add(k); }
        }
      }
      displaced = new Set(now.keys());
      for (const k of touched) meshes[k].instanceMatrix.needsUpdate = true;
    },
    nearestTo(x, z, within) {
      let best = -1, bestD = within;
      for (let i = 0; i < all.length; i++) {
        // the thing's own radius counts as reach, exactly as it does for a
        // boulder: a bone field is easier to mean than a spring
        const d = Math.hypot(posX[i] - x, posZ[i] - z) - posR[i];
        if (d < bestD) { bestD = d; best = i; }
      }
      return best < 0 ? null : { relic: all[best], y: posY[best] };
    },
    dispose() {
      for (const m of meshes) {
        group.remove(m);
        m.dispose();
      }
      for (const g of geos) g.dispose();
      material.dispose();
    },
  };
}
